import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ExportModal from "./ExportModal";

const API = "https://erp-xe9u.onrender.com/api";
export default function TimesheetTable() {
  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(
    localStorage.getItem("selectedEmployee") || "All Employees",
  );

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [expandedDays, setExpandedDays] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("timesheetExpandedDays")) || {};
    } catch {
      return {};
    }
  });
  const [selectedProject, setSelectedProject] = useState(
    localStorage.getItem("selectedProject") || "All Projects",
  );
  const [projects, setProjects] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [isPayPeriodOpen, setIsPayPeriodOpen] = useState(false);
  const userTimezone = localStorage.getItem("userTimezone") || "UTC";
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowsRef = useRef([]);
  const payPeriodRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Generate pay periods (Monday-Sunday weeks) on mount
  useEffect(() => {
    const generatePayPeriods = () => {
      const periods = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayOfWeek = today.getDay();

      // Find the upcoming or current Sunday
      const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      const currentSunday = new Date(today);
      if (dayOfWeek !== 0) {
        currentSunday.setDate(today.getDate() + daysToSunday);
      }

      // Generate 12 weeks of pay periods
      for (let i = 0; i < 12; i++) {
        const sundayEnd = new Date(currentSunday);
        sundayEnd.setDate(currentSunday.getDate() - i * 7);

        // Calculate the Monday start of this pay period
        const mondayStart = new Date(sundayEnd);
        mondayStart.setDate(sundayEnd.getDate() - 6);

        periods.push({
          value: sundayEnd.toISOString().split("T")[0],
          sundayEnd: sundayEnd,
          mondayStart: mondayStart,
          label: `${mondayStart.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })} - ${sundayEnd.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`,
          shortLabel: `Week of ${mondayStart.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}`,
        });
      }

      setPayPeriods(periods);

      // Restore saved pay period if it's still valid, otherwise default to current period
      const savedPayPeriod = localStorage.getItem("selectedPayPeriod");
      if (
        savedPayPeriod === "all" ||
        periods.some((p) => p.value === savedPayPeriod)
      ) {
        setSelectedPayPeriod(savedPayPeriod);
      } else {
        setSelectedPayPeriod(periods[0]?.value || null);
      }
    };
    generatePayPeriods();
  }, []);

  // Persist pay period selection
  useEffect(() => {
    if (selectedPayPeriod !== null) {
      localStorage.setItem("selectedPayPeriod", selectedPayPeriod);
    }
  }, [selectedPayPeriod]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        payPeriodRef.current &&
        !payPeriodRef.current.contains(event.target)
      ) {
        setIsPayPeriodOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (location.state?.timesheetSubTab) {
      setActiveTab(location.state.timesheetSubTab);
    }
  }, [location.state]);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${API}/timesheets?all=true`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const { timesheets: data } = response.data;
      setTimesheets(data || []);

      const nameToEmails = new Map();

      data.forEach((t) => {
        if (!t.displayName || !t.email) return;
        const name = t.displayName.trim();
        if (!nameToEmails.has(name)) {
          nameToEmails.set(name, new Set());
        }
        nameToEmails.get(name).add(t.email);
      });

      const uniqueEmployees = [
        { name: "All Employees", email: null, allEmails: [] },
        ...Array.from(nameToEmails.entries()).map(([name, emailSet]) => {
          const emails = Array.from(emailSet);
          return {
            name,
            email: emails[0], // displayed / selected value
            allEmails: emails, // used for filtering
          };
        }),
      ];

      setEmployees(uniqueEmployees);

      const projectNames = new Set();
      data.forEach((t) => {
        const name = t.project?.projectName?.trim();
        if (name) projectNames.add(name);
      });

      setProjects([
        "All Projects",
        ...Array.from(projectNames).sort((a, b) => a.localeCompare(b)),
      ]);
    } catch (err) {
      console.error("Error fetching timesheets:", err);
      setError(err.message || "Failed to fetch timesheets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("selectedProject", selectedProject);
  }, [selectedProject]);

  useEffect(() => {
    fetchTimesheets();
  }, []);

  // Persist employee selection
  useEffect(() => {
    localStorage.setItem("selectedEmployee", selectedEmployee);
  }, [selectedEmployee]);

  useEffect(() => {
    const tickIntervalId = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(tickIntervalId);
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, selectedEmployee, selectedPayPeriod]);

  // Helper functions
  const getTimesheetStatus = (timesheet) => {
    if (timesheet.status === false && timesheet.Rejected === false) {
      return "Pending";
    } else if (timesheet.status && timesheet.Rejected) {
      return "Rejected";
    } else if (timesheet.status === true && timesheet.Rejected === false) {
      return "Approved";
    }
    return "Pending";
  };

  const hasPendingAdjustment = (timesheet) =>
    timesheet.adjustmentRequest?.status === "pending";

  const calculateTimeDifference = (start, end, breaks = []) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const differenceInMs = endDate - startDate;
    const totalBreakDurationMs = breaks.reduce(
      (sum, breakItem) =>
        sum + (parseInt(breakItem.duration, 10) || 0) * 60 * 1000,
      0,
    );
    return Math.max(
      0,
      Math.floor((differenceInMs - totalBreakDurationMs) / 1000),
    );
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDurationLong = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // ── Unified export handler ──────────────────────────────────────────────
  // format: "excel" | "pdf"
  // range: {} (all-time) | { preset: "week"|"month"|"quarter"|"year" } |
  //        { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }
  // Both endpoints accept the same query params now, so this one function
  // drives both — no more separate fromMonth/fromYear plumbing.
  const handleExport = async ({ format, range }) => {
    if (exporting) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    setExporting(true);

    try {
      const params = new URLSearchParams();
      if (range?.preset) params.set("preset", range.preset);
      if (range?.from) params.set("from", range.from);
      if (range?.to) params.set("to", range.to);

      const path =
        format === "pdf"
          ? "/timesheets/export/approved/pdf"
          : "/timesheets/export/approved";
      const ext = format === "pdf" ? "pdf" : "xlsx";

      const url = `${API}${path}${params.toString() ? `?${params.toString()}` : ""}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Export failed: ${res.status}`);
      }
      const blob = await res.blob();
      const rangeSuffix = range?.preset
        ? `-${range.preset}`
        : range?.from
          ? `-${range.from}-to-${range.to}`
          : "";
      const anchor = document.createElement("a");
      anchor.href = URL.createObjectURL(blob);
      anchor.download = `approved-timesheets${rangeSuffix}.${ext}`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(anchor.href);
      setShowExportModal(false);
    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date) => {
    const localDate = new Date(date);
    return localDate.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: userTimezone,
    });
  };

  const formatTime = (date) => {
    const localDate = new Date(date);
    return localDate.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: userTimezone,
    });
  };

  const getDateKey = (date) => {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Pay period navigation
  const goToPreviousPeriod = () => {
    const currentIndex = payPeriods.findIndex(
      (p) => p.value === selectedPayPeriod,
    );
    if (currentIndex < payPeriods.length - 1) {
      setSelectedPayPeriod(payPeriods[currentIndex + 1].value);
    }
  };

  const goToNextPeriod = () => {
    const currentIndex = payPeriods.findIndex(
      (p) => p.value === selectedPayPeriod,
    );
    if (currentIndex > 0) {
      setSelectedPayPeriod(payPeriods[currentIndex - 1].value);
    }
  };

  const canGoNext =
    selectedPayPeriod !== "all" &&
    payPeriods.findIndex((p) => p.value === selectedPayPeriod) > 0;
  const canGoPrevious =
    selectedPayPeriod !== "all" &&
    payPeriods.findIndex((p) => p.value === selectedPayPeriod) <
      payPeriods.length - 1;

  // Filter and group timesheets
  const { groupedTimesheets, stats } = useMemo(() => {
    if (!Array.isArray(timesheets)) return { groupedTimesheets: {}, stats: {} };

    // Filter by employee and status
    let filtered = timesheets
      .filter((timesheet) => {
        if (selectedEmployee === "All Employees") return true;
        const emp = employees.find((e) => e.name === selectedEmployee);
        return emp && emp.allEmails.includes(timesheet.email);
      })
      .filter((timesheet) => {
        if (selectedProject === "All Projects") return true;
        return timesheet.project?.projectName?.trim() === selectedProject;
      })
      .filter((timesheet) => getTimesheetStatus(timesheet) === activeTab);

    // Filter by pay period
    if (selectedPayPeriod && selectedPayPeriod !== "all") {
      const period = payPeriods.find((p) => p.value === selectedPayPeriod);
      if (period) {
        const periodStart = new Date(period.mondayStart);
        periodStart.setHours(0, 0, 0, 0);
        const periodEnd = new Date(period.sundayEnd);
        periodEnd.setHours(23, 59, 59, 999);

        filtered = filtered.filter((t) => {
          const timesheetDate = new Date(t.start);
          return timesheetDate >= periodStart && timesheetDate <= periodEnd;
        });
      }
    }

    // Sort by start date descending
    filtered.sort((a, b) => new Date(b.start) - new Date(a.start));

    // Group by date
    const grouped = {};
    let totalSeconds = 0;
    let totalEntries = 0;

    filtered.forEach((timesheet) => {
      const dateKey = getDateKey(timesheet.start);
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: timesheet.start,
          entries: [],
          totalSeconds: 0,
        };
      }
      grouped[dateKey].entries.push(timesheet);

      const duration = calculateTimeDifference(
        timesheet.start,
        timesheet.end,
        timesheet.breaks || [],
      );
      grouped[dateKey].totalSeconds += duration;
      totalSeconds += duration;
      totalEntries++;
    });

    return {
      groupedTimesheets: grouped,
      stats: {
        totalSeconds,
        totalEntries,
        totalDays: Object.keys(grouped).length,
        avgPerDay:
          Object.keys(grouped).length > 0
            ? totalSeconds / Object.keys(grouped).length
            : 0,
      },
    };
  }, [
    timesheets,
    selectedEmployee,
    selectedProject,
    activeTab,
    selectedPayPeriod,
    payPeriods,
    employees,
  ]);

  const toggleDay = (dateKey) => {
    setExpandedDays((prev) => {
      const updated = { ...prev, [dateKey]: !prev[dateKey] };
      localStorage.setItem("timesheetExpandedDays", JSON.stringify(updated));
      return updated;
    });
  };

  const getId = (t) => t._id.$oid || t._id;

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectDay = (dayEntries, e) => {
    e.stopPropagation();
    const selectableIds = dayEntries
      .filter((t) => !hasPendingAdjustment(t))
      .map(getId);
    const allSelected = selectableIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableIds.forEach((id) =>
        allSelected ? next.delete(id) : next.add(id),
      );
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = Object.values(groupedTimesheets)
      .flatMap((day) => day.entries)
      .filter((t) => !hasPendingAdjustment(t))
      .map(getId);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0 || bulkUpdating) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    // Defensive: never send an id whose timesheet has a pending adjustment,
    // even if selectedIds somehow got out of sync with the disabled checkboxes.
    const allTimesheetsById = new Map(
      Object.values(groupedTimesheets)
        .flatMap((day) => day.entries)
        .map((t) => [getId(t), t]),
    );
    const idsToSend = Array.from(selectedIds).filter((id) => {
      const ts = allTimesheetsById.get(id);
      return !ts || !hasPendingAdjustment(ts);
    });

    if (idsToSend.length === 0) {
      setError(
        "All selected entries have a pending adjustment request and cannot be bulk actioned.",
      );
      return;
    }

    setBulkUpdating(true);
    try {
      const endpoint = action === "approve" ? "bulk-approve" : "bulk-reject";
      const res = await axios.post(
        `${API}/timesheets/${endpoint}`,
        { ids: idsToSend },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const { results, failedCount } = res.data;
      if (failedCount > 0) {
        const failures = results.filter((r) => !r.success);
        setError(
          `${failedCount} timesheet(s) skipped: ${failures
            .map((f) => f.message)
            .join(", ")}`,
        );
      } else {
        setError(null);
      }

      clearSelection();
      await fetchTimesheets();
    } catch (err) {
      console.error("Bulk action error:", err);
      setError(err.response?.data?.message || "Bulk action failed");
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkApprove = () => handleBulkAction("approve");
  const handleBulkReject = () => handleBulkAction("reject");

  const expandAll = () => {
    const allExpanded = {};
    Object.keys(groupedTimesheets).forEach((key) => {
      allExpanded[key] = true;
    });
    localStorage.setItem("timesheetExpandedDays", JSON.stringify(allExpanded));
    setExpandedDays(allExpanded);
  };

  const collapseAll = () => {
    localStorage.setItem("timesheetExpandedDays", JSON.stringify({}));
    setExpandedDays({});
  };

  const handleViewClick = (timesheet) => {
    const allFiltered = Object.values(groupedTimesheets).flatMap(
      (day) => day.entries,
    );
    const selectedIndex = allFiltered.findIndex(
      (t) => (t._id.$oid || t._id) === (timesheet._id.$oid || timesheet._id),
    );

    navigate(`/timesheet/${timesheet._id.$oid || timesheet._id}`, {
      state: {
        timesheets: allFiltered,
        currentTimesheetIndex: selectedIndex,
        fromProject: false,
        activeTab: activeTab,
      },
    });
  };

  // Active clock-ins for map
  const activeClockIns = useMemo(() => {
    return timesheets.filter(
      (timesheet) =>
        timesheet.clockInStatus &&
        !timesheet.end &&
        (selectedEmployee === "All Employees" ||
          timesheet.email ===
            employees.find((e) => e.name === selectedEmployee)?.email),
    );
  }, [timesheets, selectedEmployee, employees]);

  // Map effect
  useEffect(() => {
    const loadMap = () => {
      if (!window.google) return;
      const mapDiv = document.getElementById("map");
      if (!mapDiv) return;

      const defaultCenter = { lat: 43.6532, lng: -79.3832 };
      let center = defaultCenter;
      let zoom = 10;

      if (
        activeClockIns.length > 0 &&
        activeClockIns[0]?.locations?.length > 0
      ) {
        const latestLocation = activeClockIns[0].locations.reduce(
          (latest, loc) =>
            new Date(loc.timestamp) > new Date(latest.timestamp) ? loc : latest,
        );
        center = {
          lat: latestLocation.latitude,
          lng: latestLocation.longitude,
        };
        zoom = 14;
      }

      const map = new window.google.maps.Map(mapDiv, { center, zoom });
      mapRef.current = map;

      markersRef.current.forEach((marker) => marker.setMap(null));
      infoWindowsRef.current.forEach((infoWindow) => infoWindow.close());
      markersRef.current = [];
      infoWindowsRef.current = [];

      activeClockIns.forEach((timesheet) => {
        if (timesheet.locations?.length > 0) {
          const latestLocation = timesheet.locations.reduce((latest, loc) =>
            new Date(loc.timestamp) > new Date(latest.timestamp) ? loc : latest,
          );

          const marker = new window.google.maps.Marker({
            position: {
              lat: latestLocation.latitude,
              lng: latestLocation.longitude,
            },
            map,
            title: timesheet.displayName || timesheet.email,
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div class="p-2">
                <p class="font-semibold">${
                  timesheet.displayName || timesheet.email
                }</p>
                <p class="text-sm text-gray-600">${
                  timesheet.project?.projectName || "N/A"
                }</p>
                <p class="text-xs text-gray-500">Started: ${formatTime(
                  timesheet.start,
                )}</p>
              </div>
            `,
          });

          marker.addListener("click", () => {
            infoWindowsRef.current.forEach((iw) => iw.close());
            infoWindow.open(map, marker);
          });

          markersRef.current.push(marker);
          infoWindowsRef.current.push(infoWindow);
        }
      });
    };

    if (activeTab === "Pending") {
      if (window.google) {
        loadMap();
      } else {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBceM5xE-Rvc78bFnTAsOavA9qjjOGegoo&libraries=places,geometry`;
        script.async = true;
        script.onload = loadMap;
        document.head.appendChild(script);
      }
    }

    return () => {
      if (mapRef.current) {
        markersRef.current.forEach((marker) => marker.setMap(null));
        infoWindowsRef.current.forEach((infoWindow) => infoWindow.close());
        markersRef.current = [];
        infoWindowsRef.current = [];
        mapRef.current = null;
      }
    };
  }, [activeClockIns, activeTab]);

  const LiveTimer = ({ timesheet }) => {
    const duration = calculateTimeDifference(
      timesheet.start,
      null,
      timesheet.breaks || [],
    );
    return (
      <div className="flex items-center gap-2 bg-orange-500/20 text-orange-400 rounded-full px-3 py-1 border border-orange-500/30 ">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
        </span>
        <span className="font-mono text-sm">
          {formatDurationLong(duration)}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-400">Loading timesheets...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          {error}
        </div>
      </div>
    );
  }

  const currentPeriod = payPeriods.find((p) => p.value === selectedPayPeriod);

  return (
    <div className="min-h-screen mr-4 ml-4 bg-gray-900 rounded-lg p-4 mt-2">
      <div className="px-8 mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Timesheets</h1>
            <p className="text-gray-400 text-sm mt-1">
              Track and manage employee time entries
            </p>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === "Approved" && (
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white text-sm font-semibold rounded-lg shadow-md transition-all active:scale-95"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                  />
                </svg>
                Export
              </button>
            )}
            <button
              onClick={() => navigate("/AddTimesheet")}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Time Entry
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Employee Filter */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-wide">
                  Employee
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent min-w-[200px]"
                >
                  {employees.map((emp) => (
                    <option key={emp.email || "all"} value={emp.name}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Filter */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-wide">
                  Project
                </label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent min-w-[200px]"
                >
                  {projects.map((proj) => (
                    <option key={proj} value={proj}>
                      {proj}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pay Period Filter */}
              <div className="flex flex-col gap-1" ref={payPeriodRef}>
                <label className="text-xs text-gray-500 uppercase tracking-wide">
                  Pay Period
                </label>
                <div className="flex items-center gap-1">
                  {/* Previous Period Button */}
                  <button
                    onClick={goToPreviousPeriod}
                    disabled={!canGoPrevious}
                    className="p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous pay period"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsPayPeriodOpen(!isPayPeriodOpen)}
                      className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent min-w-[200px] text-left flex items-center justify-between"
                    >
                      <span>
                        {selectedPayPeriod === "all"
                          ? "All Time"
                          : currentPeriod?.label || "Select Period"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${
                          isPayPeriodOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isPayPeriodOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                        <div
                          onClick={() => {
                            setSelectedPayPeriod("all");
                            setIsPayPeriodOpen(false);
                          }}
                          className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                            selectedPayPeriod === "all"
                              ? "bg-orange-500/20 text-orange-400"
                              : "text-gray-200 hover:bg-gray-600"
                          }`}
                        >
                          All Time
                        </div>
                        <div className="border-t border-gray-600"></div>
                        {payPeriods.map((period, index) => (
                          <div
                            key={period.value}
                            onClick={() => {
                              setSelectedPayPeriod(period.value);
                              setIsPayPeriodOpen(false);
                            }}
                            className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                              selectedPayPeriod === period.value
                                ? "bg-orange-500/20 text-orange-400"
                                : "text-gray-200 hover:bg-gray-600"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{period.label}</span>
                              {index === 0 && (
                                <span className="text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">
                                  Current
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Next Period Button */}
                  <button
                    onClick={goToNextPeriod}
                    disabled={!canGoNext}
                    className="p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next pay period"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Status Tabs */}
            <div className="flex bg-gray-700/50 rounded-lg p-1">
              {["Pending", "Approved", "Rejected"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    activeTab === tab
                      ? "bg-gray-600 text-white shadow-sm"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 lg:col-span-1">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <svg
                  className="w-5 h-5 text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Pay Period
                </p>
                <p className="text-sm font-medium text-white">
                  {selectedPayPeriod === "all"
                    ? "All Time"
                    : currentPeriod?.shortLabel || "--"}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Total Hours
                </p>
                <p className="text-xl font-bold text-white">
                  {formatDuration(stats.totalSeconds)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <svg
                  className="w-5 h-5 text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Entries
                </p>
                <p className="text-xl font-bold text-white">
                  {stats.totalEntries}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <svg
                  className="w-5 h-5 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Days
                </p>
                <p className="text-xl font-bold text-white">
                  {stats.totalDays}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Avg/Day
                </p>
                <p className="text-xl font-bold text-white">
                  {formatDuration(Math.round(stats.avgPerDay))}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1 space-y-4">
            {/* Expand/Collapse Controls */}
            {activeTab === "Pending" && selectedIds.size > 0 && (
              <div className="flex items-center justify-between bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-orange-400 font-medium">
                    {selectedIds.size} selected
                  </span>
                  <button
                    onClick={clearSelection}
                    className="text-xs text-gray-400 hover:text-white underline"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleBulkReject}
                    disabled={bulkUpdating}
                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                  >
                    {bulkUpdating ? "Updating..." : "Reject"}
                  </button>
                  <button
                    onClick={handleBulkApprove}
                    disabled={bulkUpdating}
                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                  >
                    {bulkUpdating ? "Updating..." : "Approve"}
                  </button>
                </div>
              </div>
            )}
            {Object.keys(groupedTimesheets).length > 0 && (
              <div className="flex justify-between items-center">
                {activeTab === "Pending" ? (
                  <button
                    onClick={toggleSelectAll}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    {Object.values(groupedTimesheets)
                      .flatMap((d) => d.entries)
                      .every((t) => selectedIds.has(getId(t))) &&
                    selectedIds.size > 0
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <button
                    onClick={expandAll}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Expand All
                  </button>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={collapseAll}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Collapse All
                  </button>
                </div>
              </div>
            )}
            {/* Day Groups */}
            {Object.keys(groupedTimesheets).length > 0 ? (
              Object.entries(groupedTimesheets).map(([dateKey, dayData]) => (
                <div
                  key={dateKey}
                  className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
                >
                  {/* Day Header */}
                  <button
                    onClick={() => toggleDay(dateKey)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {activeTab === "Pending" && (
                        <input
                          type="checkbox"
                          checked={
                            dayData.entries.filter(
                              (t) => !hasPendingAdjustment(t),
                            ).length > 0 &&
                            dayData.entries
                              .filter((t) => !hasPendingAdjustment(t))
                              .every((t) => selectedIds.has(getId(t)))
                          }
                          onChange={() => {}}
                          onClick={(e) => toggleSelectDay(dayData.entries, e)}
                          className="w-4 h-4 rounded accent-orange-500 cursor-pointer flex-shrink-0"
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform ${
                            expandedDays[dateKey] ? "rotate-90" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        <span className="text-white font-medium">
                          {formatDate(dayData.date)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">
                        {dayData.entries.length}{" "}
                        {dayData.entries.length === 1 ? "entry" : "entries"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-400 text-sm font-mono">
                        {formatDuration(dayData.totalSeconds)}
                      </span>
                    </div>
                  </button>

                  {/* Day Entries */}
                  {expandedDays[dateKey] && (
                    <div className="border-t border-gray-700">
                      {dayData.entries.map((timesheet, idx) => {
                        const isActive =
                          timesheet.clockInStatus && !timesheet.end;
                        const duration = calculateTimeDifference(
                          timesheet.start,
                          timesheet.end,
                          timesheet.breaks || [],
                        );

                        return (
                          <div
                            key={timesheet._id.$oid || timesheet._id}
                            onClick={() => handleViewClick(timesheet)}
                            className={`px-5 py-4 flex items-center gap-4 cursor-pointer transition-colors hover:bg-gray-750 ${
                              idx !== dayData.entries.length - 1
                                ? "border-b border-gray-700/50"
                                : ""
                            } ${isActive ? "bg-orange-500/5" : ""}`}
                          >
                            {activeTab === "Pending" && (
                              <input
                                type="checkbox"
                                checked={selectedIds.has(getId(timesheet))}
                                disabled={hasPendingAdjustment(timesheet)}
                                onChange={() => {}}
                                onClick={(e) => {
                                  if (hasPendingAdjustment(timesheet)) {
                                    e.stopPropagation();
                                    return;
                                  }
                                  toggleSelect(getId(timesheet), e);
                                }}
                                title={
                                  hasPendingAdjustment(timesheet)
                                    ? "Cannot bulk approve — adjustment request pending"
                                    : undefined
                                }
                                className="w-4 h-4 rounded accent-orange-500 cursor-pointer flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                              />
                            )}
                            {/* Time Block */}
                            <div className="w-28 flex-shrink-0">
                              <div className="text-sm text-white font-medium">
                                {formatTime(timesheet.start)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {timesheet.end
                                  ? formatTime(timesheet.end)
                                  : "In Progress"}
                              </div>
                            </div>

                            {/* Project/WO Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium truncate">
                                  {timesheet.project?.projectName ||
                                    "No Project"}
                                </span>
                                {timesheet.woNumber && (
                                  <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                                    {timesheet.woNumber}
                                  </span>
                                )}
                                {hasPendingAdjustment(timesheet) && (
                                  <span className="text-xs font-medium text-orange-400 bg-orange-500/10 border border-orange-500/30 px-2 py-0.5 rounded-full whitespace-nowrap">
                                    Adjustment Requested
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-400 truncate">
                                {timesheet.title ||
                                  timesheet.workOrderDescription ||
                                  "No description"}
                              </div>
                              {selectedEmployee === "All Employees" && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {timesheet.displayName}
                                </div>
                              )}
                            </div>

                            {/* Break Info */}
                            {timesheet.breaks &&
                              timesheet.breaks.length > 0 && (
                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  {timesheet.breaks.reduce(
                                    (sum, b) => sum + (b.duration || 0),
                                    0,
                                  )}
                                  m break
                                </div>
                              )}

                            {/* Duration */}
                            <div className="w-24 flex-shrink-0 text-right">
                              {isActive ? (
                                <LiveTimer timesheet={timesheet} key={tick} />
                              ) : (
                                <span className="text-gray-300 font-mono text-sm">
                                  {formatDuration(duration)}
                                </span>
                              )}
                            </div>

                            {/* View Arrow */}
                            <svg
                              className="w-4 h-4 text-gray-500 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 text-center">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-white font-medium mb-1">
                  No {activeTab.toLowerCase()} timesheets
                </h3>
                <p className="text-gray-500 text-sm">
                  {selectedEmployee !== "All Employees"
                    ? `No ${activeTab.toLowerCase()} entries found for ${selectedEmployee}`
                    : selectedPayPeriod === "all"
                      ? `No ${activeTab.toLowerCase()} entries found`
                      : `No ${activeTab.toLowerCase()} entries found for this pay period`}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar - Active Clock-ins Map */}
          {activeTab === "Pending" && (
            <div className="lg:w-80 flex-shrink-0">
              <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden sticky top-6">
                <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-medium">Active Clock-ins</h3>
                  <span className="bg-orange-500/20 text-orange-400 text-xs font-medium px-2 py-1 rounded-full border border-orange-500/30">
                    {activeClockIns.length} active
                  </span>
                </div>
                <div id="map" className="w-full h-64"></div>
                {activeClockIns.length > 0 && (
                  <div className="p-3 border-t border-gray-700 space-y-2 max-h-48 overflow-y-auto">
                    {activeClockIns.map((timesheet) => (
                      <div
                        key={timesheet._id.$oid || timesheet._id}
                        onClick={() => handleViewClick(timesheet)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                      >
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">
                            {timesheet.displayName || timesheet.email}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {timesheet.project?.projectName || "No project"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {activeClockIns.length === 0 && (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No active clock-ins
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={(format, range) => handleExport({ format, range })}
        exporting={exporting}
        title="Export Approved Timesheets"
      />
    </div>
  );
}
